import React, {Fragment, ReactElement, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {SearchBox} from "../../components/SearchBox";
import {Route, Routes} from "react-router-dom";

import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";

import {SortColumn} from "../../api";
import {
    PartnerApiGet,
    useDeletePartnerByIdMutation,
    useGetPartnerQuery,
    usePutPartnerByIdMutation
} from "./partner_api";
import placeholder from "../../../assets/imgs/partner.jpg";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Upload from "@mui/icons-material/Upload";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";
import usePaginator from "../../hooks/usePaginator";
import {ListCardDataCol, ListGrid} from "../../components/styled";


export type PartnerItemProps = {
    item: PartnerApiGet
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

const PartnerItem = (props: PartnerItemProps) => {

    const {item, selectable, onSelect} = props;
    const [uploading, setUploading] = useState(false)
    const [put, {error}] = usePutPartnerByIdMutation()
    const [deletePartner, {error: err}] = useDeletePartnerByIdMutation()
    const onUpload = (path: string) => {
        put({body: {image_url: path}, id: item.id})
    }
    const imageUrl = useDownloadImage("partners", item.image_url)

    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card
        onClick={() => {
            selectable && onSelect && onSelect(item.id);
        }}
        sx={{
            display: "flex"
        }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={imageUrl || placeholder}
            alt={item.Name + " " + item.Code}
        />
        <ListCardDataCol>
            <CardContent>
                <Typography variant={"subtitle2"} component={"p"}
                            sx={{whiteSpace: "wrap", textOverflow: "ellipsis", wordBreak: "break-all"}}
                            color="secondary">{item.Code} {item.VatID}</Typography>
                <Typography variant={"h6"}>{item.Name}</Typography>
                <Typography variant={"body1"}>{item.documentID}</Typography>
                <Typography variant={"body1"}>{item.Description}</Typography>
            </CardContent>
            {!selectable && <CardActions>
                <Button onClick={() => setUploading(true)}
                        title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>
                <Button
                    title={item.Active ? "Onemogoči" : "Omogoči"}
                    sx={{
                        color: item.Active ? undefined : "error.main"
                    }} onClick={() => {
                    put({body: {Active: !item.Active}, id: item.id})
                }}>{!item.Active ? <Cancel/> : <DoNotDisturb/>}</Button>
                <Button title="Briši partnerja"
                        onClick={() => {
                            deletePartner({id: item.id});
                        }}
                ><Delete fontSize={"small"}/> </Button>
            </CardActions>}
        </ListCardDataCol>

        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"partners"}
            onClose={() => setUploading(false)}
        />
    </Card>
}


export type PartnerListProps =
    {
        list: PartnerApiGet[],
        selectable?: boolean;
        onSelect?: (id: number) => void;
    }

export const PartnerList = (props: PartnerListProps) => {
    const {list, onSelect, selectable} = props;


    return <ListGrid>
        {list.map(param => <PartnerItem key={param.id}
                                        item={param}
                                        onSelect={onSelect}
                                        selectable={selectable}/>
        )}
    </ListGrid>
}


type INVcControllerProps =
    {
        pageSize?: number;
        selectable?: boolean;
        onSelect?: (id: number) => void;
        customFilter?: ReactElement
    }

function buildSort(sort: string, dir: "asc" | "desc"): SortColumn[] {
    if (sort != "") {
        return [{col: sort, dir}]
    }
    return []
}

const sortFields = {
    Name: "Naziv",
    Code: "Koda",
    BarCode: "Barkoda",
    Unittext: "Enota",
    Active: "Aktiven",
}

export const PartnerListController = function (
    {
        pageSize, selectable, onSelect
    }
    : INVcControllerProps) {
    const {
        debouncedFilter,
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("partners", sortFields)


    const {data, error, isFetching} = useGetPartnerQuery({
        filter: debouncedFilter,
        pageSize: pageSize || 25,
        page: page - 1,
        sort: buildSort(sortField, sortDirection)
    });

    if (error) {
        return <SnackError error={error}/>;
    }


    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            <SearchBox
                value={filter}
                onChange={setFilter}
            />
            <SortBox
                sortDirection={sortDirection}
                sortField={sortField}
                onChangeDirection={setSortDirection}
                onSelectField={setSortField}
                fields={sortFields}
            />
        </Toolbar>
        {error && <SnackError error={error}/>}
        {(isFetching || !data) && <CircularProgress/>}
        {(!isFetching && data?.result) &&
            <PartnerList
                list={data?.result || []}
                selectable={selectable}
                onSelect={onSelect}
            />}
        <Pagination
            page={page}
            onChange={(e, page) => setPage(page)}
            count={Math.ceil((data?.count || 0) / (pageSize || 25))}
        />
    </Fragment>

}
export type INVcProps = {
    pageSize?: number;
}

export default ({pageSize = 25}: INVcProps) => {

    return <Routes>
        <Route path={"/"} element={<Fragment>
            <Title title={"Iskanje partnerjev"}/>
            <PartnerListController pageSize={pageSize}/>
        </Fragment>}/>

    </Routes>
}
